import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { Disclaimer } from "@/components/Disclaimer";
import { FREE_CTA, PREMIUM_CTA } from "@/lib/cta";


const articles = [
  { to: "/kunskapsbank/plugga-kunskapsprovet", title: "Plugga inför kunskapsprovet", desc: "4-veckorsplan, studieteknik och hur du vet när du är redo." },
  { to: "/kunskapsbank/kunskapsprovet-exempelfragor", title: "Exempelfrågor från kunskapsprovet", desc: "Fem konstruerade exempel i samma format som det riktiga provet – med facit." },
  { to: "/kunskapsbank/boka-kunskapsprovet", title: "Boka kunskapsprovet", desc: "Var, hur, kostnad och väntetider i Stockholm, Göteborg, Malmö m.fl." },
  { to: "/kunskapsbank/hur-manga-ratt-kunskapsprovet", title: "Hur många rätt krävs på kunskapsprovet?", desc: "Godkäntgränser, delprov och vad som händer om du underkänns." },
  { to: "/kunskapsbank/vanliga-misstag-kunskapsprovet", title: "Vanliga misstag på kunskapsprovet", desc: "De fällor de flesta går i – och hur du undviker dem." },
  { to: "/kunskapsbank/hur-svart-ar-kunskapsprovet", title: "Hur svårt är kunskapsprovet?", desc: "Statistik, svårighetsgrad och vad provet faktiskt testar." },
  { to: "/kunskapsbank/hur-lang-tid-tar-serveringstillstand", title: "Hur lång tid tar serveringstillstånd?", desc: "Handläggningstider i Stockholm, Göteborg, Malmö och övriga kommuner." },
  { to: "/kunskapsbank/vad-kostar-serveringstillstand", title: "Vad kostar ett serveringstillstånd?", desc: "Ansökningsavgifter, tillsynsavgifter och dolda kostnader." },
  { to: "/kunskapsbank/alkohollagen-2026-viktiga-regler", title: "Alkohollagen 2026 – viktiga regler", desc: "Vad slopas, vad gäller och vad du måste kunna inför provet." },
  { to: "/kunskapsbank/stadigvarande-vs-tillfalligt-serveringstillstand", title: "Stadigvarande vs tillfälligt tillstånd", desc: "Skillnader, när du behöver vilket och hur du kombinerar." },
  { to: "/kunskapsbank/serveringstillstand-food-truck-guide", title: "Serveringstillstånd för food truck", desc: "Komplett guide för mobil servering och festivaler." },
  { to: "/kunskapsbank/serveringstillstand-catering-guide", title: "Serveringstillstånd för catering", desc: "Cateringtillstånd för slutna sällskap, krav och ansökan." },
  { to: "/kunskapsbank/kunskapsprovet-stockholm", title: "Kunskapsprovet Stockholm", desc: "Ansökan, väntetider och provlokal i Stockholms stad." },
  { to: "/kunskapsbank/kunskapsprovet-goteborg", title: "Kunskapsprovet Göteborg", desc: "Allt om provet hos Göteborgs Stad." },
  { to: "/kunskapsbank/kunskapsprovet-malmo", title: "Kunskapsprovet Malmö", desc: "Tillståndsenheten, väntetider och tips för Malmö." },
  { to: "/kunskapsbank/underkand-pa-kunskapsprovet", title: "Underkänd på kunskapsprovet", desc: "Vad gör du nu? Omprov, avgifter och tips inför nästa försök." },
  { to: "/kunskapsbank/pbi-kretsen-forklaring", title: "PBI-kretsen förklarad", desc: "Vilka personer omfattas och vad granskar kommunen?" },
  { to: "/kunskapsbank/serveringstillstand-nattklubb", title: "Serveringstillstånd för nattklubb", desc: "Utökade tider, ordningsvakter och mat-kravet 2026." },
  { to: "/kunskapsbank/serveringstillstand-festival", title: "Serveringstillstånd för festival", desc: "Tillfälligt tillstånd, säkerhet och tidplan för arrangörer." },
  { to: "/kunskapsbank/serveringstillstand-2026-guide", title: "Serveringstillstånd 2026 – komplett guide", desc: "Steg-för-steg: ansökan, kostnad, nya regler och handläggningstider." },
  { to: "/kunskapsbank/alkoholtillstand-utan-mat", title: "Alkoholtillstånd utan mat 2026", desc: "Matkravet slopas 1 juni 2026 – så fungerar det nya regelverket för barer." },
  { to: "/kunskapsbank/alkoholtillstand-regler", title: "Alkoholtillstånd – regler", desc: "Lämplighet, lokal, servering, marknadsföring och tillsyn i en översikt." },
  { to: "/kunskapsbank/ansoka-om-alkoholtillstand", title: "Ansöka om alkoholtillstånd", desc: "Steg-för-steg, dokumentchecklista och tidplan." },
  { to: "/kunskapsbank/soka-alkoholtillstand", title: "Söka alkoholtillstånd", desc: "Var, när och hur du söker – och vad du gör vid avslag." },
  { to: "/kunskapsbank/nya-alkohollagen-2026", title: "Nya alkohollagen 2026", desc: "Slopat matkrav, gårdsförsäljning och uppdaterat kunskapsprov." },
  { to: "/kunskapsbank/kunskapsprovet-orebro", title: "Kunskapsprovet Örebro", desc: "Ansökan, väntetider och kostnader hos Örebro kommun." },
  { to: "/kunskapsbank/kunskapsprovet-jonkoping", title: "Kunskapsprovet Jönköping", desc: "Provet, e-tjänsten och tips för Jönköping." },
  { to: "/kunskapsbank/kunskapsprovet-umea", title: "Kunskapsprovet Umeå", desc: "Tillståndsenheten, säsong och studentnära verksamheter." },
  { to: "/kunskapsbank/kunskapsprovet-vaxjo", title: "Kunskapsprovet Växjö", desc: "Allt om provet hos Växjö kommun." },
  { to: "/kunskapsbank/restaurang-lon-regler-personal", title: "Restauranglön & åldersregler för personal", desc: "Minimilön, OB-tillägg och 18-/20-årsgränserna – vad krögare behöver veta." },
] as const;

export const Route = createFileRoute("/kunskapsbank/")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Kunskapsbank – guider om serveringstillstånd & kunskapsprovet" },
      { name: "description", content: "Kunskapsbank med fördjupande SEO-artiklar om serveringstillstånd, alkohollagen 2026 och kunskapsprovet. Svar på de vanligaste frågorna." },
      { property: "og:title", content: "Kunskapsbank – serveringstillstånd & kunskapsprov" },
      { property: "og:description", content: "Fördjupande guider och artiklar för alla som söker serveringstillstånd eller pluggar inför kunskapsprovet." },
      { property: "og:url", content: "https://alkoholprovet.se/kunskapsbank" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/kunskapsbank" }],
    scripts: [
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Kunskapsbank", url: "https://alkoholprovet.se/kunskapsbank" },
      ]),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Kunskapsbank",
          url: "https://alkoholprovet.se/kunskapsbank",
          hasPart: articles.map((a) => ({
            "@type": "Article",
            headline: a.title,
            url: `https://alkoholprovet.se${a.to}`,
          })),
        }),
      },
    ],
  }),
});

function Page() {
  return (
    <article className="container-narrow py-12 sm:py-16">
      <Breadcrumbs items={[{ label: "Hem", to: "/" }, { label: "Kunskapsbank" }]} />
      <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs">
        <BookOpen className="h-3.5 w-3.5" /> Kunskapsbank
      </span>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight text-balance">
        Kunskapsbank – serveringstillstånd & kunskapsprovet
      </h1>
      <p className="mt-4 text-lg text-muted-foreground text-balance">
        Fördjupande guider om alkohollagen, ansökan om serveringstillstånd och hur du klarar kunskapsprovet på första försöket. Uppdaterat för 2026 års regler.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <Link to={FREE_CTA.to} className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          {FREE_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to={PREMIUM_CTA.to} className="inline-flex items-center gap-2 rounded-xl border border-accent/40 bg-accent/10 px-5 py-3 text-sm font-medium text-accent hover:bg-accent/15">
          {PREMIUM_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
        <Link to="/serveringstillstand-och-kunskapsprov" className="inline-flex rounded-xl border border-border px-5 py-3 text-sm hover:bg-secondary">
          Se hub för alla guider
        </Link>
      </div>


      <div className="mt-12 grid gap-4 sm:grid-cols-2">
        {articles.map((a) => (
          <Link key={a.to} to={a.to} className="group rounded-2xl border border-border bg-card p-6 hover:border-accent transition-colors">
            <h2 className="font-display text-lg font-semibold group-hover:text-accent">{a.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground">{a.desc}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-accent">
              Läs artikeln <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        ))}
      </div>

      <div className="mt-12 rounded-2xl bg-surface-deep text-primary-foreground p-8 text-center">
        <h2 className="font-display text-2xl font-bold">Plugga inför kunskapsprovet</h2>
        <p className="mt-2 text-primary-foreground/80">Börja gratis utan kort. Lås upp obegränsat med Premium.</p>
        <Link to={FREE_CTA.to} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-white text-primary px-6 py-3 text-sm font-medium hover:bg-white/90">
          {FREE_CTA.label} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>


      <div className="mt-8 rounded-2xl border border-accent/30 bg-accent/5 p-5 sm:p-6">
        <h2 className="font-display text-lg font-semibold">Alkoholtillstånd prov gratis</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Vill du testa direkt? Kör vårt <Link to="/gratis-ovningsfragor-serveringstillstand" className="text-accent underline underline-offset-2 hover:no-underline">gratis prov för alkoholtillstånd</Link> – fem riktiga
          övningsfrågor med förklaring och lagrum, utan registrering.
        </p>
      </div>
      <div className="mt-10">
        <Disclaimer />
      </div>
    </article>
  );
}
